/**
 * Marketing analytics: Google Analytics 4 + Meta Pixel.
 *
 * Privacy rules (see marketing/ads/README.md):
 * - Nothing loads until the visitor accepts the cookie banner.
 * - Only marketing / demo / signup / checkout pages are tracked. Guest-facing pages
 *   (invitations, RSVP, galleries, moments) and the couple's dashboard never
 *   send page views, so guests are never tracked.
 * - Both IDs come from env (REACT_APP_GA4_ID, REACT_APP_META_PIXEL_ID); with
 *   neither set, analytics is fully off and no banner is shown.
 */

export const CONSENT_KEY = 'handfastly-consent';
export const CONSENT_EVENT = 'handfastly:consent';
const PENDING_CHECKOUT_KEY = 'handfastly-pending-checkout';
const TRACKED_PURCHASES_KEY = 'handfastly-tracked-purchases';
const MAX_REMEMBERED_PURCHASES = 20;
const CURRENCY = 'USD';

const TRACKED_PATHS = [
  /^\/$/,
  /^\/pricing\/?$/,
  /^\/demo(\/live)?\/?$/,
  /^\/auth\/(login|register)\/?$/,
  /^\/payment\/(success|cancel)\/?$/,
];

function config() {
  return {
    gaId: process.env.REACT_APP_GA4_ID || '',
    pixelId: process.env.REACT_APP_META_PIXEL_ID || '',
  };
}

function storage(kind) {
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch (_) {
    return null;
  }
}

function readJson(kind, key, fallback) {
  try {
    const raw = storage(kind)?.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

function writeJson(kind, key, value) {
  try {
    storage(kind)?.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* storage blocked — tracking is best-effort */
  }
}

export function isAnalyticsConfigured() {
  const { gaId, pixelId } = config();
  return Boolean(gaId || pixelId);
}

export function isTrackedPath(pathname) {
  return TRACKED_PATHS.some((re) => re.test(pathname || ''));
}

/** 'granted' | 'denied' | null (not asked yet). */
export function getConsent() {
  try {
    const value = storage('local')?.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (_) {
    return null;
  }
}

function hasConsent() {
  return isAnalyticsConfigured() && getConsent() === 'granted';
}

let loaded = false;

function loadGa(gaId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // Page views are sent manually so untracked routes never report.
  window.gtag('config', gaId, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
  document.head.appendChild(script);
}

function loadPixel(pixelId) {
  if (!window.fbq) {
    const fbq = function fbq() {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    // Stop the pixel from auto-sending PageView on SPA route changes.
    fbq.disablePushState = true;
    window.fbq = fbq;
    window._fbq = fbq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  // No automatic button-click / form events — only the events sent below.
  window.fbq('set', 'autoConfig', false, pixelId);
  window.fbq('init', pixelId);
}

function ensureLoaded() {
  if (loaded || !hasConsent()) return loaded;
  const { gaId, pixelId } = config();
  if (gaId) loadGa(gaId);
  if (pixelId) loadPixel(pixelId);
  loaded = true;
  return true;
}

export function setConsent(value) {
  if (value !== 'granted' && value !== 'denied') return;
  try {
    storage('local')?.setItem(CONSENT_KEY, value);
  } catch (_) {
    /* ignore */
  }
  if (value === 'granted') {
    ensureLoaded();
  } else if (loaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied' });
    window.fbq?.('consent', 'revoke');
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

/** Clears the stored choice so the banner asks again (footer "Cookie settings"). */
export function resetConsent() {
  try {
    storage('local')?.removeItem(CONSENT_KEY);
  } catch (_) {
    /* ignore */
  }
  if (loaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied', ad_storage: 'denied' });
    window.fbq?.('consent', 'revoke');
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
}

function send({ ga, meta }) {
  if (!ensureLoaded()) return;
  const { gaId, pixelId } = config();
  if (gaId && ga) window.gtag('event', ga.name, ga.params || {});
  if (pixelId && meta) {
    const args = [meta.custom ? 'trackCustom' : 'track', meta.name, meta.params || {}];
    if (meta.eventId) args.push({ eventID: meta.eventId });
    window.fbq(...args);
  }
}

/** Current URL with the Stripe session id stripped (UTM / fbclid kept for attribution). */
function cleanLocation() {
  const url = new URL(window.location.href);
  url.searchParams.delete('session_id');
  return url.toString();
}

const DEMO_PATH = /^\/demo(\/live)?\/?$/;

export function trackPageView(pathname) {
  if (!isTrackedPath(pathname)) return;
  send({
    ga: { name: 'page_view', params: { page_location: cleanLocation(), page_title: document.title } },
    meta: { name: 'PageView' },
  });
  const demo = DEMO_PATH.exec(pathname);
  if (demo) trackDemoView(demo[1] ? 'live' : 'invitation');
}

/** ViewContent on /demo and /demo/live: the "engaged visitor" signal ads optimise for. */
function trackDemoView(kind) {
  const template = kind === 'invitation'
    ? new URLSearchParams(window.location.search).get('template') || 'default'
    : 'live';
  send({
    ga: { name: 'view_demo', params: { demo: kind, template } },
    meta: {
      name: 'ViewContent',
      params: { content_name: `demo_${kind}`, content_category: 'demo', content_ids: [template] },
    },
  });
}

/**
 * Click on a CTA inside a demo (e.g. "Create yours" → register, "See live" → /demo/live).
 * @param {{ from: 'invitation'|'live', to: 'register'|'live' }} opts
 */
export function trackDemoCta({ from, to }) {
  const params = { demo: from, target: to };
  send({
    ga: { name: 'demo_cta_click', params },
    meta: { name: 'DemoCtaClick', params, custom: true },
  });
}

/** @param {string} [eventId] shared with the server's CAPI copy so Meta dedups the two */
export function trackSignUp(eventId) {
  send({
    ga: { name: 'sign_up', params: { method: 'email' } },
    meta: { name: 'CompleteRegistration', eventId },
  });
}

function readCookie(name) {
  try {
    const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  } catch (_) {
    return undefined;
  }
}

/**
 * Consent + Meta browser ids for the backend's Conversions API copy of our
 * events. The _fbp / _fbc cookies are read only after the visitor accepted.
 * @returns {{ granted: boolean, fbp?: string, fbc?: string } | undefined}
 */
export function getAdSignals() {
  if (!isAnalyticsConfigured()) return undefined;
  if (getConsent() !== 'granted') return { granted: false };
  const ids = [['fbp', readCookie('_fbp')], ['fbc', readCookie('_fbc')]].filter(([, v]) => v);
  return { granted: true, ...Object.fromEntries(ids) };
}

/** Event id shared by the Pixel and the server event (Meta dedups on it). */
export function newEventId() {
  try {
    return window.crypto?.randomUUID?.();
  } catch (_) {
    return undefined;
  }
}

/**
 * Remembers the chosen plan for the purchase event (Stripe redirects back in
 * the same tab, so sessionStorage survives) and reports the checkout start.
 */
export function trackBeginCheckout({ planId, value }) {
  const amount = Number(value) || 0;
  writeJson('session', PENDING_CHECKOUT_KEY, { planId, value: amount });
  const item = { item_id: planId, item_name: planId, price: amount, quantity: 1 };
  send({
    ga: { name: 'begin_checkout', params: { currency: CURRENCY, value: amount, items: [item] } },
    meta: {
      name: 'InitiateCheckout',
      params: { currency: CURRENCY, value: amount, content_ids: [planId], content_type: 'product' },
    },
  });
}

/** Sends the purchase once per Stripe session, even if the success page is reloaded. */
export function trackPurchase(sessionId) {
  if (!sessionId || !hasConsent()) return;
  const done = readJson('local', TRACKED_PURCHASES_KEY, []);
  if (done.includes(sessionId)) return;
  const pending = readJson('session', PENDING_CHECKOUT_KEY, null);
  const planId = pending?.planId || 'unknown';
  const amount = Number(pending?.value) || 0;
  const item = { item_id: planId, item_name: planId, price: amount, quantity: 1 };
  send({
    ga: {
      name: 'purchase',
      params: { transaction_id: sessionId, currency: CURRENCY, value: amount, items: [item] },
    },
    meta: {
      name: 'Purchase',
      params: { currency: CURRENCY, value: amount, content_ids: [planId], content_type: 'product' },
      eventId: sessionId,
    },
  });
  writeJson('local', TRACKED_PURCHASES_KEY, [...done, sessionId].slice(-MAX_REMEMBERED_PURCHASES));
  try {
    storage('session')?.removeItem(PENDING_CHECKOUT_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Test-only: forget that scripts were loaded. */
export function __resetForTests() {
  loaded = false;
}
